import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth, can, userRoles, hasRole } from "@/context/AuthContext";
import StoreOrderAlerts from "@/components/StoreOrderAlerts";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  House, Package, ArrowsClockwise, ChartBar, Gear, SignOut, Camera,
  UsersThree, UserList, Receipt, Kanban, Bank, ChartLineUp, ShoppingBag, ShoppingCart,
  List, X, Megaphone, ChartPieSlice, Storefront, Ticket, MegaphoneSimple, Stack, Sparkle,
  Folder, FolderSimplePlus, CaretRight, CaretDown, PencilSimple, Check, Trash, DotsSixVertical, ArrowCounterClockwise,
  Star, StarHalf, ArrowLineLeft, ArrowLineRight, UploadSimple, Clock, Hourglass, FirstAidKit, Wrench,
  ImageSquare, Tray, CalendarBlank,
} from "@phosphor-icons/react";

const ALL_NAV = [
  { to: "/marketing", label: "Marketing · Fotos", icon: Camera, testid: "nav-marketing", area: "marketing" },
  { to: "/finance", label: "Dashboard Financiero", icon: ChartLineUp, testid: "nav-finance", area: "finance" },
  { to: "/reporte-mensual", label: "Reporte mensual", icon: CalendarBlank, testid: "nav-monthly-report", area: "finance", superOnly: true },
  { to: "/metricos", label: "Métricos financieros", icon: ChartBar, testid: "nav-metrics", area: "finance", superOnly: true },
  { to: "/dashboard", label: "Dashboard de Inventario", icon: House, end: true, testid: "nav-dashboard", area: "dashboard" },
  { to: "/products", label: "Productos", icon: Package, testid: "nav-products", area: "products" },
  { to: "/preinventario", label: "Preinventario", icon: Tray, testid: "nav-preinventory", area: "preinventory" },
  { to: "/movements", label: "Movimientos", icon: ArrowsClockwise, testid: "nav-movements", area: "movements" },
  { to: "/sales", label: "Notas de venta", icon: Receipt, testid: "nav-sales", area: "sales" },
  { to: "/publications", label: "Publicaciones", icon: Megaphone, testid: "nav-publications", area: "publications" },
  { to: "/publications-overview", label: "Publicaciones · Resumen", icon: ChartPieSlice, testid: "nav-publications-overview", area: "publications_overview" },
  { to: "/portal-orders", label: "Solicitudes portal", icon: ShoppingCart, testid: "nav-portal-orders", area: "portal_orders" },
  { to: "/online-orders", label: "Pedidos · Tienda", icon: Storefront, testid: "nav-online-orders", area: "online_orders" },
  { to: "/coupons", label: "Cupones", icon: Ticket, testid: "nav-coupons", area: "marketing_admin" },
  { to: "/promotions", label: "Promociones", icon: MegaphoneSimple, testid: "nav-promotions", area: "marketing_admin" },
  { to: "/combos", label: "Combos", icon: Package, testid: "nav-combos", area: "marketing_admin" },
  { to: "/groupings", label: "Agrupaciones", icon: Stack, testid: "nav-groupings", area: "marketing_admin" },
  { to: "/ads", label: "Ads", icon: Megaphone, testid: "nav-ads", area: "marketing_admin" },
  { to: "/banners", label: "Banners de tienda", icon: ImageSquare, testid: "nav-banners", area: "settings", superOnly: true },
  { to: "/promotions/analytics", label: "Analítica · Promos", icon: ChartPieSlice, testid: "nav-promotions-analytics", area: "marketing_admin" },
  { to: "/research", label: "Price Research", icon: ChartLineUp, testid: "nav-research", area: "price_research" },
  { to: "/insights", label: "Insights · Alendra AI", icon: Sparkle, testid: "nav-insights", area: "alendra_ai", superOnly: true },
  { to: "/mis-tareas", label: "Mis Tareas", icon: UserList, testid: "nav-mis-tareas", area: "_tasks_", alwaysShow: true },
  { to: "/hr/empleados", label: "Empleados", icon: UsersThree, testid: "nav-hr-employees", area: "hr", superOnly: true },
  { to: "/hr/reporte-semanal", label: "Reporte semanal RH", icon: ChartBar, testid: "nav-hr-weekly", area: "hr", superOnly: true },
  { to: "/hr/autorizaciones", label: "Autorizaciones RH", icon: Hourglass, testid: "nav-hr-auth", area: "hr", superOnly: true },
  { to: "/reloj-checador", label: "Abrir Reloj Checador", icon: Clock, testid: "nav-hr-kiosk", area: "hr", superOnly: true, external: true },
  { to: "/hospital", label: "Taller y Reparación", icon: FirstAidKit, testid: "nav-hospital", area: "inventory" },
  { to: "/hospital-equipos", label: "Hospital Interno", icon: FirstAidKit, testid: "nav-patients", area: "inventory" },
  { to: "/mi-tablero", label: "Mi Tablero (Técnico)", icon: Wrench, testid: "nav-my-tech", area: "inventory" },
  { to: "/clients", label: "Clientes", icon: UserList, testid: "nav-clients", area: "clients" },
  { to: "/crm", label: "CRM", icon: Kanban, testid: "nav-crm", area: "clients" },
  { to: "/banks", label: "Bancos", icon: Bank, testid: "nav-banks", area: "banks" },
  { to: "/expenses", label: "Gastos", icon: ShoppingBag, testid: "nav-expenses", area: "expenses" },
  { to: "/gastos-recurrentes", label: "Gastos recurrentes", icon: ArrowsClockwise, testid: "nav-recurring-expenses", area: "expenses", superOnly: true },
  { to: "/expenses/new", label: "Registrar gasto", icon: ShoppingBag, testid: "nav-expenses-add", area: "expenses_add", capOnly: true },
  { to: "/reports", label: "Reportes", icon: ChartBar, testid: "nav-reports", area: "reports" },
  { to: "/users", label: "Usuarios", icon: UsersThree, testid: "nav-users", area: "users" },
  { to: "/settings", label: "Ajustes", icon: Gear, testid: "nav-settings", area: "settings" },
];

const ROLE_LABEL = {
  super_admin: { text: "Super Admin", color: "#002FA7" },
  inventory: { text: "Inventario", color: "#00873D" },
  sales: { text: "Ventas", color: "#FFC000" },
  marketing: { text: "Marketing", color: "#E91E63" },
};

const NAV_BY_TESTID = ALL_NAV.reduce((acc, n) => { acc[n.testid] = n; return acc; }, {});

// Compute which nav items are visible to the given user, honouring all the
// role/capability rules previously enforced inline.
function computeVisible(user) {
  const isSalesOnly = hasRole(user, "sales") && !hasRole(user, "super_admin") && !hasRole(user, "inventory");
  // Roles operativos de taller: no participan en el flujo de tareas ni en
  // módulos de inteligencia, así que ocultamos "Mis Tareas" (que normalmente
  // es alwaysShow) para no dejar un folder "Inteligencia" vacío en su sidebar.
  const roles = userRoles(user);
  const isWorkshopOnly = roles.length > 0
    && roles.every((r) => r === "technician" || r === "taller");
  return ALL_NAV.filter((n) => {
    if (isWorkshopOnly && n.testid === "nav-mis-tareas") return false;
    if (isWorkshopOnly && n.testid === "nav-dashboard") return false;
    if (n.alwaysShow) return true;
    if (n.area === "products" && isSalesOnly) return true;
    if (n.superOnly) return hasRole(user, "super_admin") && can(user, n.area);
    if (n.capOnly) {
      const hasFullArea = can(user, "expenses");
      return !hasFullArea && can(user, n.area);
    }
    return can(user, n.area);
  }).map((n) => (isSalesOnly && n.to === "/dashboard" ? { ...n, label: "Dashboard" } : n));
}

// Given a layout and the list of visible testids, return the folder list to
// render. Items in the layout that the user can't see are dropped silently;
// items the user CAN see but that are not in any folder are gathered into a
// fallback "Sistema" folder so newly-added modules never go missing.
function applyLayout(layout, visibleTestids, { editMode = false } = {}) {
  const visibleSet = new Set(visibleTestids);
  const seen = new Set();
  const folders = (layout?.folders || []).map((f) => {
    const rawItems = f.items || [];
    const items = rawItems.filter((t) => visibleSet.has(t));
    items.forEach((t) => seen.add(t));
    // A folder is "user-empty" when the user themselves left it empty (e.g.
    // just created it via "Nueva carpeta"). We keep those visible in edit
    // mode so the user can drop items into them.
    return { ...f, items, _wasEmpty: rawItems.length === 0 };
  });
  const orphans = visibleTestids.filter((t) => !seen.has(t));
  if (orphans.length > 0) {
    const existing = folders.find((f) => f.id === "f-system");
    if (existing) existing.items = [...existing.items, ...orphans];
    else folders.push({ id: "f-system", name: "Sistema", collapsed: false, items: orphans, _wasEmpty: false });
  }
  if (editMode) {
    // In edit mode, hide folders that only exist because of the default
    // layout but contain no items this user can see (e.g. "Ventas" folder
    // for a technician). User-created empty folders (`_wasEmpty`) remain
    // visible so the user can fill them.
    return folders.filter((f) => f.items.length > 0 || f._wasEmpty || f.id === "f-system");
  }
  return folders.filter((f) => f.items.length > 0 || f.id === "f-system");
}

function uid() {
  return "f-" + Math.random().toString(36).slice(2, 10);
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [portalPending, setPortalPending] = useState(0);
  const [tasksPending, setTasksPending] = useState(0);
  const [hrAuthPending, setHrAuthPending] = useState(0);
  const [recurringPending, setRecurringPending] = useState(0);
  const [hospitalPending, setHospitalPending] = useState(0);
  const recurringNotifiedRef = useRef(false);
  const pollRef = useRef(null);
  const tasksPollRef = useRef(null);
  const hrAuthPollRef = useRef(null);

  // Sidebar layout state
  const [layout, setLayout] = useState(null);   // { folders, favorites, compact }
  const [editMode, setEditMode] = useState(false);
  const [draftLayout, setDraftLayout] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // collapsedOverrides keeps the toggle state in view mode without dirtying the saved layout
  const [collapsedOverrides, setCollapsedOverrides] = useState({});
  const dragRef = useRef(null);   // {type, payload}

  useEffect(() => {
    if (!user) return;
    api.get("/sidebar/layout").then((r) => setLayout(r.data.layout)).catch(() => setLayout({ folders: [] }));
  }, [user]);

  // Notification badges polling (unchanged behaviour)
  useEffect(() => {
    if (!user || !can(user, "portal_orders")) return undefined;
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const { data } = await api.get("/portal-orders/stats");
        if (!cancelled) setPortalPending(data.pending || 0);
      } catch { /* ignore */ }
    };
    fetchStats();
    pollRef.current = setInterval(fetchStats, 30000);
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const fetchTasks = async () => {
      try {
        const { data } = await api.get("/alendra/tasks/pending-count");
        if (!cancelled) setTasksPending(data.pending || 0);
      } catch { /* ignore */ }
    };
    fetchTasks();
    tasksPollRef.current = setInterval(fetchTasks, 60000);
    return () => { cancelled = true; if (tasksPollRef.current) clearInterval(tasksPollRef.current); };
  }, [user]);

  useEffect(() => {
    if (location.pathname === "/mis-tareas") {
      api.get("/alendra/tasks/pending-count").then((r) => setTasksPending(r.data.pending || 0)).catch(() => {});
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!user || !hasRole(user, "super_admin")) return undefined;
    let cancelled = false;
    const fetchHr = async () => {
      try {
        const { data } = await api.get("/hr/authorizations/pending-count");
        if (!cancelled) setHrAuthPending(data.pending || 0);
      } catch { /* ignore */ }
    };
    fetchHr();
    hrAuthPollRef.current = setInterval(fetchHr, 30000);
    return () => { cancelled = true; if (hrAuthPollRef.current) clearInterval(hrAuthPollRef.current); };
  }, [user]);

  useEffect(() => {
    if (location.pathname === "/hr/autorizaciones") {
      api.get("/hr/authorizations/pending-count").then((r) => setHrAuthPending(r.data.pending || 0)).catch(() => {});
    }
  }, [location.pathname]);

  // Recurring expenses pending (overdue + due today) + in-app toast on first detection
  useEffect(() => {
    if (!user || !hasRole(user, "super_admin")) return undefined;
    let cancelled = false;
    const fetchRecurring = async () => {
      try {
        const { data } = await api.get("/recurring-expenses/pending-count");
        if (cancelled) return;
        const n = data.pending || 0;
        setRecurringPending(n);
        if (n > 0 && !recurringNotifiedRef.current) {
          recurringNotifiedRef.current = true;
          toast.warning(`${n} gasto${n === 1 ? "" : "s"} recurrente${n === 1 ? "" : "s"} ${n === 1 ? "vence" : "vencen"} hoy o antes`, {
            description: "Revísalos y confirma sus pagos para mantener tus libros al día.",
            action: { label: "Ver", onClick: () => navigate("/gastos-recurrentes") },
            duration: 10000,
          });
        }
      } catch { /* ignore */ }
    };
    fetchRecurring();
    const interval = setInterval(fetchRecurring, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, navigate]);

  useEffect(() => {
    if (location.pathname === "/gastos-recurrentes") {
      api.get("/recurring-expenses/pending-count").then((r) => setRecurringPending(r.data.pending || 0)).catch(() => {});
    }
  }, [location.pathname]);

  // Hospital pending tickets (badge)
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const fetchHospital = async () => {
      try {
        const { data } = await api.get("/hospital/pending-count");
        if (!cancelled) setHospitalPending(data.pending || 0);
      } catch { /* ignore */ }
    };
    fetchHospital();
    const interval = setInterval(fetchHospital, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  useEffect(() => {
    if (location.pathname === "/hospital") {
      api.get("/hospital/pending-count").then((r) => setHospitalPending(r.data.pending || 0)).catch(() => {});
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/portal-orders" && can(user, "portal_orders")) {
      api.get("/portal-orders/stats").then((r) => setPortalPending(r.data.pending || 0)).catch(() => {});
    }
  }, [location.pathname, user]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const visible = useMemo(() => (user ? computeVisible(user) : []), [user]);
  const visibleIds = useMemo(() => visible.map((v) => v.testid), [visible]);

  // Layout being rendered (edit-time draft takes priority)
  const activeLayout = editMode ? draftLayout : layout;
  const renderedFolders = useMemo(
    () => applyLayout(activeLayout || { folders: [] }, visibleIds, { editMode }),
    [activeLayout, visibleIds, editMode],
  );
  const favorites = useMemo(() => {
    const favs = activeLayout?.favorites || [];
    const visSet = new Set(visibleIds);
    return favs.filter((t) => visSet.has(t) && NAV_BY_TESTID[t]);
  }, [activeLayout, visibleIds]);
  const compact = !!activeLayout?.compact;

  // ─── Edit-mode helpers ─────────────────────────────────────────────
  const startEdit = () => {
    setDraftLayout(JSON.parse(JSON.stringify(layout || { folders: [], favorites: [], compact: false })));
    setEditMode(true);
  };
  const cancelEdit = () => { setDraftLayout(null); setEditMode(false); };
  const saveLayout = async () => {
    if (!draftLayout) return;
    setSaving(true);
    try {
      const { data } = await api.put("/sidebar/layout", {
        folders: draftLayout.folders || [],
        favorites: draftLayout.favorites || [],
        compact: !!draftLayout.compact,
      });
      setLayout(data.layout);
      setEditMode(false);
      setDraftLayout(null);
      toast.success("Menú actualizado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el menú");
    } finally { setSaving(false); }
  };
  const resetToDefault = async () => {
    if (!confirm("¿Restablecer el menú al orden automático?")) return;
    try {
      const { data } = await api.delete("/sidebar/layout");
      setLayout(data.layout);
      setDraftLayout(JSON.parse(JSON.stringify(data.layout)));
      toast.success("Menú restablecido al automático");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const publishAsDefault = async () => {
    if (!confirm("Publicar esta organización como default global. Solo afecta a usuarios que NO han personalizado su menú. ¿Continuar?")) return;
    setPublishing(true);
    try {
      // First persist the current draft, then publish it
      await api.put("/sidebar/layout", {
        folders: draftLayout.folders || [],
        favorites: draftLayout.favorites || [],
        compact: !!draftLayout.compact,
      });
      await api.post("/sidebar/publish-default");
      toast.success("Default global publicado ✓");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo publicar");
    } finally { setPublishing(false); }
  };

  // Toggle favorite — works both in view and edit mode (auto-save when in view).
  const toggleFavorite = async (testid) => {
    const currentFavs = (editMode ? draftLayout : layout)?.favorites || [];
    const isFav = currentFavs.includes(testid);
    const nextFavs = isFav ? currentFavs.filter((t) => t !== testid) : [...currentFavs, testid];
    if (editMode) {
      updateDraft((d) => { d.favorites = nextFavs; });
    } else {
      const optimistic = { ...layout, favorites: nextFavs };
      setLayout(optimistic);
      try {
        await api.put("/sidebar/layout", {
          folders: layout.folders || [],
          favorites: nextFavs,
          compact: !!layout.compact,
        });
      } catch (err) {
        setLayout(layout);
        toast.error("No se pudo actualizar favoritos");
      }
    }
  };

  // Toggle compact mode (persists immediately when not in edit mode).
  const toggleCompact = async () => {
    if (editMode) {
      updateDraft((d) => { d.compact = !d.compact; });
      return;
    }
    const next = !layout?.compact;
    const optimistic = { ...layout, compact: next };
    setLayout(optimistic);
    try {
      await api.put("/sidebar/layout", {
        folders: layout.folders || [],
        favorites: layout.favorites || [],
        compact: next,
      });
    } catch {
      setLayout(layout);
      toast.error("No se pudo cambiar la vista");
    }
  };

  const updateDraft = useCallback((mutator) => {
    setDraftLayout((prev) => {
      const next = JSON.parse(JSON.stringify(prev || { folders: [] }));
      mutator(next);
      return next;
    });
  }, []);

  const [folderPrompt, setFolderPrompt] = useState(null); // { mode: 'create'|'rename', fid?, initialName }
  const [folderPromptValue, setFolderPromptValue] = useState("");
  const [deletePrompt, setDeletePrompt] = useState(null); // { fid, name, count }

  const addFolder = () => {
    setFolderPromptValue("");
    setFolderPrompt({ mode: "create" });
  };
  const renameFolder = (fid) => {
    const folder = draftLayout?.folders?.find((f) => f.id === fid);
    setFolderPromptValue(folder?.name || "");
    setFolderPrompt({ mode: "rename", fid, initialName: folder?.name || "" });
  };
  const submitFolderPrompt = () => {
    const name = (folderPromptValue || "").trim();
    if (!name) { setFolderPrompt(null); return; }
    if (folderPrompt?.mode === "create") {
      updateDraft((d) => { d.folders.push({ id: uid(), name, items: [], collapsed: false }); });
    } else if (folderPrompt?.mode === "rename" && folderPrompt.fid) {
      updateDraft((d) => {
        const f = d.folders.find((x) => x.id === folderPrompt.fid);
        if (f) f.name = name;
      });
    }
    setFolderPrompt(null);
  };

  const deleteFolder = (fid) => {
    const folder = draftLayout?.folders?.find((f) => f.id === fid);
    if (!folder) return;
    if (folder.items.length > 0) {
      setDeletePrompt({ fid, name: folder.name, count: folder.items.length });
      return;
    }
    updateDraft((d) => { d.folders = d.folders.filter((f) => f.id !== fid); });
  };
  const confirmDeleteFolder = () => {
    if (!deletePrompt) return;
    updateDraft((d) => {
      const target = d.folders.find((f) => f.id === deletePrompt.fid);
      const rescued = target?.items || [];
      // Ensure a Sistema folder exists to hold the rescued items.
      let sys = d.folders.find((f) => f.id === "f-system");
      if (!sys) {
        sys = { id: "f-system", name: "Sistema", collapsed: false, items: [] };
        d.folders.push(sys);
      }
      // Merge unique items into Sistema (prevent duplicates).
      const existing = new Set(sys.items || []);
      rescued.forEach((t) => { if (!existing.has(t)) sys.items.push(t); });
      d.folders = d.folders.filter((f) => f.id !== deletePrompt.fid);
    });
    setDeletePrompt(null);
  };

  const toggleCollapse = (fid) => {
    if (editMode) {
      updateDraft((d) => { const f = d.folders.find((x) => x.id === fid); if (f) f.collapsed = !f.collapsed; });
    } else {
      setCollapsedOverrides((prev) => ({ ...prev, [fid]: !isFolderCollapsed(fid) }));
    }
  };
  const isFolderCollapsed = (fid) => {
    if (collapsedOverrides[fid] !== undefined) return collapsedOverrides[fid];
    const f = renderedFolders.find((x) => x.id === fid);
    return !!f?.collapsed;
  };

  // ─── HTML5 drag & drop helpers ─────────────────────────────────────
  const onItemDragStart = (folderId, testid, e) => {
    if (!editMode) return;
    dragRef.current = { type: "item", folderId, testid };
    e.dataTransfer.effectAllowed = "move";
  };
  const onFolderDragStart = (folderId, e) => {
    if (!editMode) return;
    dragRef.current = { type: "folder", folderId };
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { if (editMode) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } };
  // Drop a nav item over another item (insert before/after) or over the folder body (push to end).
  const onItemDropAt = (targetFolderId, targetTestid) => {
    const d = dragRef.current;
    if (!d || d.type !== "item") return;
    updateDraft((draft) => {
      const fromFolder = draft.folders.find((f) => f.id === d.folderId) || ensureSystemFolder(draft);
      fromFolder.items = (fromFolder.items || []).filter((t) => t !== d.testid);
      const toFolder = draft.folders.find((f) => f.id === targetFolderId) || ensureSystemFolder(draft);
      if (!targetTestid) {
        toFolder.items.push(d.testid);
      } else {
        const idx = toFolder.items.indexOf(targetTestid);
        toFolder.items.splice(idx === -1 ? toFolder.items.length : idx, 0, d.testid);
      }
    });
    dragRef.current = null;
  };
  const onFolderDropAt = (targetFolderId) => {
    const d = dragRef.current;
    if (!d || d.type !== "folder") return;
    updateDraft((draft) => {
      const fromIdx = draft.folders.findIndex((f) => f.id === d.folderId);
      const toIdx = draft.folders.findIndex((f) => f.id === targetFolderId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
      const [moved] = draft.folders.splice(fromIdx, 1);
      draft.folders.splice(toIdx, 0, moved);
    });
    dragRef.current = null;
  };

  const roles = userRoles(user);
  const primaryRoleLabel = ROLE_LABEL[roles[0]] || { text: roles[0] || "—", color: "#52525B" };

  return (
    <div className="min-h-screen flex bg-white">
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} data-testid="sidebar-backdrop"/>
      )}

      <aside
        className={`fixed lg:static top-0 left-0 z-50 h-full lg:h-auto ${compact ? "w-16 lg:w-16" : "w-72 lg:w-60"} max-w-[85vw] bg-white border-r border-zinc-200 flex flex-col flex-shrink-0 shadow-xl lg:shadow-none transition-all duration-200 ease-out ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
        data-compact={compact ? "true" : "false"}
      >
        <div className={`${compact ? "p-2" : "p-5"} border-b border-zinc-200 flex items-center ${compact ? "justify-center" : "justify-between"} gap-2`}>
          {compact ? (
            <button onClick={toggleCompact} title="Expandir menú" data-testid="sidebar-expand-btn" className="p-2 rounded hover:bg-zinc-100 text-zinc-600">
              <ArrowLineRight size={18} weight="bold"/>
            </button>
          ) : (
            <>
              <div>
                <div className="font-mono-data text-[10px] tracking-widest text-zinc-500">INVENTARIO</div>
                <div className="font-heading text-lg font-extrabold tracking-tight">Gadgets · Cómputo</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="lg:hidden p-1 -mr-1 text-zinc-500 hover:text-zinc-900" data-testid="sidebar-close-btn" aria-label="Cerrar menú">
                <X size={20} />
              </button>
            </>
          )}
        </div>

        {/* Edit toolbar */}
        {!compact && (
        <div className="px-3 pt-3 pb-1">
          {!editMode ? (
            <div className="flex gap-1">
              <button
                type="button"
                onClick={startEdit}
                className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2 py-1.5 border border-zinc-200 rounded text-zinc-600 hover:bg-zinc-50"
                data-testid="sidebar-organize-btn"
              >
                <Folder size={13} weight="duotone"/> Organizar menú
              </button>
              <button
                type="button"
                onClick={toggleCompact}
                title="Vista compacta"
                className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-2 py-1.5 border border-zinc-200 rounded text-zinc-600 hover:bg-zinc-50"
                data-testid="sidebar-compact-btn"
              >
                <ArrowLineLeft size={13}/>
              </button>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex gap-1">
                <button onClick={addFolder} className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-2 py-1.5 bg-[#002FA7] text-white rounded hover:bg-[#001a6a]" data-testid="sidebar-add-folder-btn">
                  <FolderSimplePlus size={13}/> Nueva carpeta
                </button>
                <button onClick={resetToDefault} className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-2 py-1.5 border border-zinc-200 rounded text-zinc-700 hover:bg-zinc-50" data-testid="sidebar-reset-btn" title="Restablecer al automático">
                  <ArrowCounterClockwise size={13}/>
                </button>
              </div>
              <div className="flex gap-1">
                <button onClick={saveLayout} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-2 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50" data-testid="sidebar-save-btn">
                  <Check size={13}/> {saving ? "Guardando…" : "Guardar"}
                </button>
                <button onClick={cancelEdit} className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-2 py-1.5 border border-zinc-200 rounded text-zinc-700 hover:bg-zinc-50" data-testid="sidebar-cancel-btn">
                  Cancelar
                </button>
              </div>
              {hasRole(user, "super_admin") && (
                <button onClick={publishAsDefault} disabled={publishing} className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-2 py-1.5 bg-[#7E57C2] text-white rounded hover:bg-[#6442b8] disabled:opacity-50" data-testid="sidebar-publish-btn" title="Publicar como default global para todo el equipo">
                  <UploadSimple size={13}/> {publishing ? "Publicando…" : "Publicar como default"}
                </button>
              )}
            </div>
          )}
        </div>
        )}

        <nav className={`flex-1 ${compact ? "px-1.5 py-2" : "px-3 py-2"} space-y-1 overflow-y-auto`} data-testid="sidebar-nav">
          {/* COMPACT MODE: flat icon list */}
          {compact && (
            <div className="space-y-1">
              {favorites.map((testid) => {
                const nav = NAV_BY_TESTID[testid]; if (!nav) return null;
                return <CompactNavItem key={`fav-${testid}`} nav={nav} testid={testid} pinned/>;
              })}
              {favorites.length > 0 && <div className="my-1 mx-2 h-px bg-zinc-200"/>}
              {renderedFolders.map((folder) =>
                folder.items.filter((t) => !favorites.includes(t)).map((testid) => {
                  const nav = NAV_BY_TESTID[testid]; if (!nav) return null;
                  return <CompactNavItem key={testid} nav={nav} testid={testid}/>;
                })
              )}
            </div>
          )}

          {/* FULL MODE: favorites section + folders */}
          {!compact && favorites.length > 0 && (
            <div className="mb-3" data-testid="sidebar-favorites">
              <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-700">
                <Star size={13} weight="fill" className="text-amber-500"/>
                <span className="flex-1">Favoritos</span>
                <span className="text-[9px] text-zinc-400 font-mono-data">{favorites.length}</span>
              </div>
              <div className="space-y-0.5 pl-1">
                {favorites.map((testid) => {
                  const nav = NAV_BY_TESTID[testid]; if (!nav) return null;
                  const Icon = nav.icon;
                  let badge = null, badgeTestid = null;
                  if (nav.to === "/portal-orders" && portalPending > 0) { badge = portalPending; badgeTestid = "portal-orders-badge"; }
                  else if (nav.to === "/mis-tareas" && tasksPending > 0) { badge = tasksPending; badgeTestid = "tasks-pending-badge"; }
                  else if (nav.to === "/gastos-recurrentes" && recurringPending > 0) { badge = recurringPending; badgeTestid = "recurring-pending-badge"; }
                  else if (nav.to === "/hospital" && hospitalPending > 0) { badge = hospitalPending; badgeTestid = "hospital-pending-badge"; }
                  return (
                    <div key={testid} className="group relative">
                      <NavLink to={nav.to} end={nav.end} data-testid={`fav-${testid}`} onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-1.5 rounded text-sm font-medium transition-all duration-150 ${isActive ? "bg-amber-500 text-white" : "text-zinc-700 hover:bg-amber-50"}`}>
                        <Icon size={16} weight="duotone"/>
                        <span className="flex-1 truncate">{nav.label}</span>
                        {badge !== null && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold bg-[#E0000F] text-white rounded-full" data-testid={badgeTestid || undefined}>
                            {badge > 99 ? "99+" : badge}
                          </span>
                        )}
                      </NavLink>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(testid); }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 opacity-0 group-hover:opacity-100 hover:bg-white rounded" title="Quitar de favoritos" data-testid={`unfav-${testid}`}>
                        <Star size={12} weight="fill" className="text-amber-500"/>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!compact && renderedFolders.map((folder) => {
            const collapsed = isFolderCollapsed(folder.id);
            return (
              <div
                key={folder.id}
                onDragOver={onDragOver}
                onDrop={() => {
                  if (dragRef.current?.type === "folder") onFolderDropAt(folder.id);
                  else if (dragRef.current?.type === "item") onItemDropAt(folder.id, null);
                }}
                data-testid={`sidebar-folder-${folder.id}`}
              >
                <div
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-bold uppercase tracking-wider text-zinc-500 ${editMode ? "bg-zinc-50 cursor-grab" : "cursor-pointer hover:bg-zinc-50"}`}
                  draggable={editMode && folder.id !== "f-system"}
                  onDragStart={(e) => onFolderDragStart(folder.id, e)}
                  onClick={() => toggleCollapse(folder.id)}
                  data-testid={`sidebar-folder-header-${folder.id}`}
                >
                  {editMode && folder.id !== "f-system" && <DotsSixVertical size={12} className="text-zinc-300 shrink-0"/>}
                  {collapsed ? <CaretRight size={12} weight="bold"/> : <CaretDown size={12} weight="bold"/>}
                  <Folder size={13} weight="duotone" className="text-[#002FA7]"/>
                  <span className="flex-1 truncate text-[11px]">{folder.name}</span>
                  <span className="text-[9px] text-zinc-400 font-mono-data">{folder.items.length}</span>
                  {editMode && folder.id !== "f-system" && (
                    <span className="flex gap-0.5">
                      <button type="button" onClick={(e) => { e.stopPropagation(); renameFolder(folder.id); }} className="p-0.5 text-zinc-400 hover:text-[#002FA7]" title="Renombrar" data-testid={`sidebar-rename-${folder.id}`}>
                        <PencilSimple size={11}/>
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }} className="p-0.5 text-zinc-400 hover:text-red-600" title="Eliminar" data-testid={`sidebar-delete-${folder.id}`}>
                        <Trash size={11}/>
                      </button>
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <div className="mt-1 space-y-0.5 pl-1">
                    {folder.items.map((testid) => {
                      const nav = NAV_BY_TESTID[testid];
                      if (!nav) return null;
                      const isFav = favorites.includes(testid);
                      let badge = null;
                      let badgeTestid = null;
                      if (nav.to === "/portal-orders" && portalPending > 0) { badge = portalPending; badgeTestid = "portal-orders-badge"; }
                      else if (nav.to === "/mis-tareas" && tasksPending > 0) { badge = tasksPending; badgeTestid = "tasks-pending-badge"; }
                      else if (nav.to === "/hr/autorizaciones" && hrAuthPending > 0) { badge = hrAuthPending; badgeTestid = "hr-auth-badge"; }
                      else if (nav.to === "/gastos-recurrentes" && recurringPending > 0) { badge = recurringPending; badgeTestid = "recurring-pending-badge"; }
                      else if (nav.to === "/hospital" && hospitalPending > 0) { badge = hospitalPending; badgeTestid = "hospital-pending-badge"; }
                      const Icon = nav.icon;
                      return (
                        <div
                          key={testid}
                          draggable={editMode}
                          onDragStart={(e) => onItemDragStart(folder.id, testid, e)}
                          onDragOver={onDragOver}
                          onDrop={(e) => { e.stopPropagation(); onItemDropAt(folder.id, testid); }}
                          className="group relative"
                        >
                          {editMode ? (
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded text-sm text-zinc-700 bg-white border border-dashed border-zinc-200 cursor-grab" data-testid={`sidebar-item-${testid}`}>
                              <DotsSixVertical size={12} className="text-zinc-300 shrink-0"/>
                              <Icon size={16} weight="duotone"/>
                              <span className="flex-1 truncate">{nav.label}</span>
                              <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleFavorite(testid); }} className="p-0.5" title={isFav ? "Quitar de favoritos" : "Marcar como favorito"} data-testid={`star-${testid}`}>
                                <Star size={12} weight={isFav ? "fill" : "regular"} className={isFav ? "text-amber-500" : "text-zinc-300 hover:text-amber-500"}/>
                              </button>
                            </div>
                          ) : nav.external ? (
                            <a href={nav.to} target="_blank" rel="noopener noreferrer" data-testid={testid}
                              className="flex items-center gap-2.5 px-3 py-1.5 rounded text-sm font-medium text-zinc-700 hover:bg-zinc-100">
                              <Icon size={16} weight="duotone"/>
                              <span className="flex-1 truncate">{nav.label}</span>
                              <ArrowLineRight size={11} className="text-zinc-400"/>
                            </a>
                          ) : (
                            <>
                              <NavLink
                                to={nav.to}
                                end={nav.end}
                                data-testid={testid}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                  `flex items-center gap-2.5 px-3 py-1.5 rounded text-sm font-medium transition-all duration-150 ${isActive ? "bg-[#002FA7] text-white" : "text-zinc-700 hover:bg-zinc-100"}`
                                }
                              >
                                <Icon size={16} weight="duotone"/>
                                <span className="flex-1 truncate">{nav.label}</span>
                                {badge !== null && (
                                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold bg-[#E0000F] text-white rounded-full mr-5" data-testid={badgeTestid || undefined} aria-label={`${badge} pendientes`}>
                                    {badge > 99 ? "99+" : badge}
                                  </span>
                                )}
                              </NavLink>
                              <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(testid); }}
                                className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded ${isFav ? "opacity-100" : "opacity-0 group-hover:opacity-100"} hover:bg-white`}
                                title={isFav ? "Quitar de favoritos" : "Marcar como favorito"} data-testid={`fav-toggle-${testid}`}>
                                <Star size={12} weight={isFav ? "fill" : "regular"} className={isFav ? "text-amber-500" : "text-zinc-400"}/>
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                    {editMode && folder.items.length === 0 && (
                      <div className="px-3 py-3 text-[11px] text-zinc-400 italic border border-dashed border-zinc-200 rounded text-center">Arrastra módulos aquí</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={`${compact ? "p-2" : "p-3"} border-t border-zinc-200`}>
          {compact ? (
            <button onClick={handleLogout} title="Cerrar sesión" data-testid="logout-btn" className="w-full p-2 flex justify-center text-zinc-600 hover:bg-zinc-100 rounded">
              <SignOut size={18}/>
            </button>
          ) : (
            <>
              <div className="px-3 py-2 mb-2">
                <div className="text-xs text-zinc-500 font-mono-data">CONECTADO</div>
                <div className="text-sm font-medium truncate" data-testid="user-email">{user?.name}</div>
                <div className="text-[11px] text-zinc-400 truncate">{user?.email}</div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-sm" style={{ background: primaryRoleLabel.color + "20", color: primaryRoleLabel.color }}>
                    {primaryRoleLabel.text}
                  </span>
                  {roles.slice(1).map((r) => {
                    const lbl = ROLE_LABEL[r] || { text: r, color: "#52525B" };
                    return (
                      <span key={r} className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-sm" style={{ background: lbl.color + "20", color: lbl.color }}>
                        {lbl.text}
                      </span>
                    );
                  })}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn" className="w-full justify-start gap-2">
                <SignOut size={16} /> Cerrar sesión
              </Button>
            </>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-4 py-3 border-b border-zinc-200 bg-white">
          <button type="button" onClick={() => setOpen(true)} className="p-1.5 -ml-1.5 rounded hover:bg-zinc-100" data-testid="sidebar-open-btn" aria-label="Abrir menú">
            <List size={22} />
          </button>
          <div className="text-center min-w-0">
            <div className="font-mono-data text-[9px] tracking-widest text-zinc-500 leading-none">INVENTARIO</div>
            <div className="font-heading text-sm font-extrabold tracking-tight truncate">Gadgets · Cómputo</div>
          </div>
          <div className="w-8" />
        </header>
        <div className="flex-1 min-w-0 overflow-x-auto"><Outlet /></div>
      </main>
      <StoreOrderAlerts />

      {/* Folder create/rename dialog (replaces window.prompt which is blocked in iframes) */}
      <Dialog open={!!folderPrompt} onOpenChange={(v) => !v && setFolderPrompt(null)}>
        <DialogContent className="sm:max-w-md" data-testid="folder-prompt-dialog">
          <DialogHeader>
            <DialogTitle>{folderPrompt?.mode === "rename" ? "Renombrar carpeta" : "Nueva carpeta"}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Input
              autoFocus
              value={folderPromptValue}
              onChange={(e) => setFolderPromptValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitFolderPrompt(); } if (e.key === "Escape") setFolderPrompt(null); }}
              placeholder="Nombre de la carpeta"
              data-testid="folder-prompt-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderPrompt(null)} data-testid="folder-prompt-cancel">Cancelar</Button>
            <Button onClick={submitFolderPrompt} disabled={!folderPromptValue.trim()} className="bg-[#002FA7] hover:bg-[#001a6a]" data-testid="folder-prompt-submit">
              {folderPrompt?.mode === "rename" ? "Renombrar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-folder confirm dialog */}
      <Dialog open={!!deletePrompt} onOpenChange={(v) => !v && setDeletePrompt(null)}>
        <DialogContent className="sm:max-w-md" data-testid="folder-delete-dialog">
          <DialogHeader>
            <DialogTitle>Eliminar carpeta</DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm text-zinc-700">
            La carpeta <b>{deletePrompt?.name && `«${deletePrompt.name}»`}</b> tiene {deletePrompt?.count} módulo(s). Se moverán a la carpeta «Sistema». ¿Continuar?
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePrompt(null)}>Cancelar</Button>
            <Button onClick={confirmDeleteFolder} className="bg-[#E0000F] hover:bg-[#b8000c]" data-testid="folder-delete-confirm">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ensureSystemFolder(draft) {
  let sys = draft.folders.find((f) => f.id === "f-system");
  if (!sys) {
    sys = { id: "f-system", name: "Sistema", collapsed: false, items: [] };
    draft.folders.push(sys);
  }
  return sys;
}

function CompactNavItem({ nav, testid, pinned }) {
  const Icon = nav.icon;
  return (
    <NavLink
      to={nav.to}
      end={nav.end}
      title={nav.label}
      data-testid={`compact-${testid}`}
      className={({ isActive }) =>
        `flex items-center justify-center w-full h-10 rounded relative transition-all ${isActive ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-zinc-100"}`
      }
    >
      <Icon size={18} weight="duotone"/>
      {pinned && (
        <span className="absolute top-0.5 right-0.5">
          <Star size={8} weight="fill" className="text-amber-500"/>
        </span>
      )}
    </NavLink>
  );
}
